import axiosIns from '@axiosIns'
import axios from 'axios'
import Vue from 'vue'

const DEFAULT_CURRENT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50
let cancelTokenSource = null

const cancelPendingApiRequests = () => {
  if (cancelTokenSource) {
    cancelTokenSource.cancel()
  }
  cancelTokenSource = axios.CancelToken.source()
}

export default {
  namespaced: true,
  state: {
    activeYoungId: null,
    messages: [],
    youngs: [],
    blockedYoungs: [],
    LastContacts: [],
    youngsComment: [],
    blockedBy: {},
    excelDatas: null,
    zipDatas: null,
    pagination: {
      currentPage: DEFAULT_CURRENT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      hasNext: false,
      hasPrevious: false,
    },
    searchParams: {
      searchInput: '',
      filterByMissedCalls: false,
    },
    isInitialLoad: true,
  },
  getters: {
    getLastContact: state => id => state.LastContacts.find(lastContact => lastContact.id === id),
    activeYoung: (state, getters) => (state.activeYoungId
        ? getters.youngs.find(y => y.id === state.activeYoungId)
        : {}),
    messages: (state, getters) => state.messages
        .map(message => ({
          ...message,
          user: message.userId ? getters.getLastContact(message.userId) : null,
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date)),
    youngsComment: state => state.youngsComment,
    youngs: (state, getters) => {
      const processedYoungs = state.youngs.map(young => ({
        ...young,
        lastMessageUser: young.lastMessageUserId
          ? getters.getLastContact(young.lastMessageUserId)
          : null,
      }))
      // Le tri est maintenant géré par l'API lorsque filterByMissedCalls est false.
      // Si filterByMissedCalls est true, l'API fournit déjà le bon ordre.
      return processedYoungs
    },
    blockedYoungs: (state, getters) => {
      const processedYoungs = state.blockedYoungs.map(young => ({
        ...young,
        lastMessageUser: young.lastMessageUserId
          ? getters.getLastContact(young.lastMessageUserId)
          : null,
      }))
      // Le tri est maintenant géré par l'API lorsque filterByMissedCalls est false.
      // Si filterByMissedCalls est true, l'API fournit déjà le bon ordre.
      return processedYoungs
    },
    getBlockedBy: state => state.blockedBy,
    excelDatas: state => state.excelDatas,
    zipDatas: state => state.zipDatas,
    pagination: state => state.pagination,
    searchParams: state => state.searchParams,
  },
  mutations: {
    addOrUpdateMessage(state, message) {
      if (state.activeYoungId === message.youngId) {
        state.messages = state.messages
          .filter(m => m.id !== message.id)
          .concat([message])
      }
      const young = state.youngs.find(y => y.id === message.youngId)
      if (young) {
        Vue.set(young, 'lastMessageDate', message.date)
        if (message.userId && young.lastMessageUserId !== message.userId) {
          Vue.set(young, 'lastMessageUserId', message.userId)
        }
      }
    },
    addOrUpdateYoung(state, young) {
      state.youngs = state.youngs
        .filter(y => y.id !== young.id)
        .concat([young])
    },
    deleteMessage(state, id) {
      state.messages = state.messages.filter(m => m.id !== id)
    },
    deleteYoung(state, id) {
      if (state.activeYoungId === id) {
        state.activeYoungId = null
      }
      state.youngs = state.youngs.filter(y => y.id !== id)
      state.messages = state.messages.filter(m => m.youngId !== id)
    },
    setActiveYoungId(state, id) {
      state.activeYoungId = id
      state.messages = state.messages.filter(m => m.youngId === id)
    },
    updateMessages(state, messages) {
      const allMessages = state.messages
        .filter(m => m.youngId === state.activeYoungId)
        .concat(messages)
      const distinctMessages = []
      const idMap = new Map()
      allMessages.forEach(message => {
        if (!idMap.has(message.id)) {
          idMap.set(message.id, true)
          distinctMessages.push(message)
        }
      })
      state.messages = distinctMessages
    },
    updateYoungs(state, youngs) {
      state.youngs = youngs
    },
    appendYoungs(state, youngs) {
      // Append new youngs to the existing array, avoiding duplicates
      const youngIds = new Set(state.youngs.map(y => y.id))
      const newYoungs = youngs.filter(y => !youngIds.has(y.id))
      state.youngs = [...state.youngs, ...newYoungs]
    },
    updatePagination(state, { responseDataLength, pageNumber, pageSize }) {
      state.pagination.currentPage = pageNumber
      state.pagination.pageSize = pageSize
      state.pagination.hasNext = responseDataLength === pageSize && responseDataLength > 0
      state.pagination.hasPrevious = pageNumber > 1
    },
    updateSearchParams(state, params) {
      state.searchParams = {
        searchInput:
          params.searchInput !== undefined
            ? params.searchInput
            : state.searchParams.searchInput,
        filterByMissedCalls:
          params.filterByMissedCalls !== undefined
            ? params.filterByMissedCalls
            : state.searchParams.filterByMissedCalls,
      }
    },
    changeStatusToPending(state, id) {
      const foundIndex = state.youngs.findIndex(x => x.id === id)
      state.youngs[foundIndex].conversationStatus = 1
    },
    fetchedYoungComments(state, comments) {
      state.youngsComment = comments
    },
    fetchedBlockedYoungs(state, blockedYoungs) {
      state.blockedYoungs = blockedYoungs
    },
    updatedComment() {},
    updateYoung(state, updatedYoung) {
      state.youngs = state.youngs.map(young => (young.id === updatedYoung.id ? { ...young, ...updatedYoung } : young))
    },
    addedComment(state, comment) {
      state.youngsComment.push(comment)
    },
    fetchedBlockedBy(state, blockedBy) {
      state.blockedBy = blockedBy
    },
    updateExcelDatas(state, excelDatas) {
      state.excelDatas = excelDatas
    },
    updateZipDatas(state, zipDatas) {
      state.zipDatas = zipDatas
    },
    fetchLastContacts(state, LastContacts) {
      state.LastContacts = LastContacts
    },
    clearYoungs(state) {
      state.youngs = []
      state.activeYoungId = null
      state.pagination.currentPage = DEFAULT_CURRENT_PAGE
      state.pagination.hasNext = false
    },
    setInitialLoadStatus(state, status) {
      state.isInitialLoad = status
    },
  },
  actions: {
    getYoungIdsOrderedByLastMissedCall() {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/GetYoungIdsOrderedByLastMissedCall')
          .then(response => {
            resolve(response.data)
          })
          .catch(error => reject(error))
      })
    },
    addOrUpdateMessage({ commit }, message) {
      commit('addOrUpdateMessage', message)
    },
    addOrUpdateYoung({ commit }, young) {
      commit('addOrUpdateYoung', young)
    },
    deleteYoung({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .delete(`Young/${youngId}`)
          .then(response => {
            commit('deleteYoung', youngId)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    setActiveYoungId({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        cancelPendingApiRequests()
        commit('setActiveYoungId', youngId)
        axiosIns
          .get(`Young/${youngId}/Messages`, {
            cancelToken: cancelTokenSource.token,
          })
          .then(response => {
            commit('updateMessages', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchYoungs({ commit, state }, params = {}) {
      return new Promise((resolve, reject) => {
        cancelPendingApiRequests()

        const pageNumber = params.page || 1
        const pageSize = params.pageSize || state.pagination.pageSize

        let apiSearchInput = ''
        let apiFilterByMissedCalls = false

        if (pageNumber === 1) {
          apiSearchInput = params.searchQuery !== undefined ? params.searchQuery : ''
          apiFilterByMissedCalls = params.isFilteringByAbsencesCalls !== undefined
              ? params.isFilteringByAbsencesCalls
              : false

          commit('updateSearchParams', {
            searchInput: apiSearchInput,
            filterByMissedCalls: apiFilterByMissedCalls,
          })
          commit('clearYoungs')
        } else {
          apiSearchInput = state.searchParams.searchInput
          apiFilterByMissedCalls = state.searchParams.filterByMissedCalls
        }

        const queryParams = new URLSearchParams({
          pageNumber,
          pageSize,
          searchInput: apiSearchInput,
          filterByMissedCalls: apiFilterByMissedCalls,
        })

        axiosIns
          .get(`Young?${queryParams.toString()}`, {
            cancelToken: cancelTokenSource.token,
          })
          .then(response => {
            commit('updatePagination', {
              responseDataLength: response.data.length,
              pageNumber,
              pageSize,
            })

            if (pageNumber === 1) {
              commit('updateYoungs', response.data)
            } else {
              commit('appendYoungs', response.data)
            }
            resolve(response)
          })
          .catch(error => {
            if (!axios.isCancel(error)) {
              reject(error)
            } else {
              resolve({ data: [] })
            }
          })
      })
    },
    fetchBlockedYoungs({ commit }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/blocked')
          .then(response => {
            commit('fetchedBlockedYoungs', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    changeConversationStatus({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .put(`Young/${youngId}/PendingConversation`)
          .then(response => {
            commit('changeStatusToPending', youngId)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchYoungComments({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`Young/${youngId}/Comments`)
          .then(response => {
            commit('fetchedYoungComments', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    updateComment({ commit }, data) {
      const body = {
        comment: data.comment,
        commentId: data.commentId,
      }
      return new Promise((resolve, reject) => {
        axiosIns
          .put(`Young/${data.youngId}/UpdateComment`, body)
          .then(response => {
            commit('updatedComment', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    addComment({ commit }, data) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${data.youngId}/AddComment`, data.comment, {
            headers: { 'Content-Type': 'application/json' },
          })
          .then(response => {
            commit('addedComment', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    updateYoungDetails({ commit }, data) {
      return new Promise((resolve, reject) => {
        axiosIns
          .put('Young/', data)
          .then(response => {
            commit('updateYoung', data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchBlockedBy({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`Young/${youngId}/BlockedBy`)
          .then(response => {
            commit('fetchedBlockedBy', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchExcelDatas({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${youngId}/ExportExcel`, {}, { responseType: 'blob' })
          .then(response => {
            commit('updateExcelDatas', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchZipDatas({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${youngId}/ExportZip`, {}, { responseType: 'blob' })
          .then(response => {
            commit('updateZipDatas', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchLastContacts({ commit }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/LastContacts')
          .then(response => {
            commit('fetchLastContacts', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
  },
}
